import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OBSOverlay() {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const style = searchParams.get('style') || 'full';
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchOBSData();
    // Poll every 2 seconds
    const interval = setInterval(fetchOBSData, 2000);
    return () => clearInterval(interval);
  }, [matchId, style]);

  const fetchOBSData = async () => {
    try {
      const response = await axios.get(`${API}/obs/match/${matchId}?style=${style}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching OBS data:", error);
    }
  };

  if (!data) {
    return (
      <div style={{ 
        background: 'transparent', 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        Loading...
      </div>
    );
  }

  // Minimal style - just scores
  if (style === 'minimal') {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(10, 13, 18, 0.95) 0%, rgba(15, 20, 30, 0.95) 100%)',
        padding: '20px 40px',
        borderRadius: '12px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '30px',
        fontFamily: 'Outfit, sans-serif',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ 
            color: 'white', 
            fontSize: '18px', 
            margin: 0,
            fontWeight: 500 
          }}>
            {data.player1.name}
          </p>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <span style={{ 
            color: '#2db97d', 
            fontSize: '48px', 
            fontWeight: 'bold' 
          }}>
            {data.player1.frames_won}
          </span>
          <span style={{ color: '#666', fontSize: '32px' }}>-</span>
          <span style={{ 
            color: '#2db97d', 
            fontSize: '48px', 
            fontWeight: 'bold' 
          }}>
            {data.player2.frames_won}
          </span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ 
            color: 'white', 
            fontSize: '18px', 
            margin: 0,
            fontWeight: 500 
          }}>
            {data.player2.name}
          </p>
        </div>
      </div>
    );
  }

  // Full style - with photos and frame scores
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(10, 13, 18, 0.98) 0%, rgba(15, 20, 30, 0.98) 100%)',
      padding: '24px 32px',
      borderRadius: '16px',
      fontFamily: 'Outfit, sans-serif',
      boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.1)',
      minWidth: '600px'
    }}>
      {/* Tournament Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <span style={{ color: '#2db97d', fontSize: '14px', fontWeight: 600 }}>
          {data.tournament_name}
        </span>
        <span style={{ 
          color: '#999', 
          fontSize: '12px', 
          textTransform: 'capitalize' 
        }}>
          {data.round?.replace('_', ' ')} • Best of {data.best_of}
        </span>
        {data.status === 'live' && (
          <span style={{
            background: 'rgba(239, 68, 68, 0.2)',
            color: '#f87171',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#ef4444',
              animation: 'pulse 2s infinite'
            }}></span>
            LIVE
          </span>
        )}
      </div>

      {/* Players */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Player 1 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          flex: 1
        }}>
          {data.player1.photo && (
            <img 
              src={data.player1.photo} 
              alt={data.player1.name}
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid rgba(45, 185, 125, 0.3)'
              }}
            />
          )}
          <div>
            <p style={{ 
              color: 'white', 
              fontSize: '20px', 
              margin: 0,
              fontWeight: 600 
            }}>
              {data.player1.name}
            </p>
          </div>
        </div>

        {/* Score */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '0 32px'
        }}>
          <span style={{ 
            color: '#2db97d', 
            fontSize: '56px', 
            fontWeight: 'bold',
            lineHeight: 1
          }}>
            {data.player1.frames_won}
          </span>
          <span style={{ 
            color: '#444', 
            fontSize: '36px',
            fontWeight: 300
          }}>-</span>
          <span style={{ 
            color: '#2db97d', 
            fontSize: '56px', 
            fontWeight: 'bold',
            lineHeight: 1
          }}>
            {data.player2.frames_won}
          </span>
        </div>

        {/* Player 2 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          flex: 1,
          justifyContent: 'flex-end'
        }}>
          <div style={{ textAlign: 'right' }}>
            <p style={{ 
              color: 'white', 
              fontSize: '20px', 
              margin: 0,
              fontWeight: 600 
            }}>
              {data.player2.name}
            </p>
          </div>
          {data.player2.photo && (
            <img 
              src={data.player2.photo} 
              alt={data.player2.name}
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid rgba(45, 185, 125, 0.3)'
              }}
            />
          )}
        </div>
      </div>

      {/* Current Frame Score */}
      {data.current_frame && (
        <div style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '24px'
        }}>
          <span style={{ color: '#666', fontSize: '12px' }}>Frame {data.current_frame.number}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#daa520', fontSize: '24px', fontWeight: 'bold' }}>
              {data.current_frame.player1_points}
            </span>
            <span style={{ color: '#444' }}>-</span>
            <span style={{ color: '#daa520', fontSize: '24px', fontWeight: 'bold' }}>
              {data.current_frame.player2_points}
            </span>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
}
